<template>
  <v-row justify="center">
    <v-dialog v-model="dialogVisible" persistent max-width="1000">
      <v-card>
        <v-toolbar dark color="primary">
          <v-btn icon dark @click.stop="closeDialog()">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title>Booking Details</v-toolbar-title>
          <div class="flex-grow-1"></div>
          <v-toolbar-items> </v-toolbar-items>
        </v-toolbar>
        <v-alert
          v-model="alert.show"
          :dismissible="alert.dismissible"
          :type="alert.type"
          transition="fade-transition"
          class="ma-4"
        >
          {{ alert.text }}
        </v-alert>
        <div v-if="infoText" v-html="infoText" class="pa-4"></div>
        <v-form v-if="showForm" ref="form" v-model="form" class="pa-4 pt-6">
          <v-row>
            <v-col>
              <v-menu
                v-model="datepicker"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                max-width="290px"
                min-width="290px"
                :nudge-right="50"
                v-if="!selectMultipleDates"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="formattedDate"
                    label="Market Date"
                    prepend-icon="mdi-calendar-range"
                    readonly
                    v-on="on"
                    :disabled="formData.deleted_date ? true : (bookingType == 'invitation' && formAction == 'update')"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="formData.market_date"
                  :allowed-dates="allowedDates"
                  @input="datepicker = false"
                  @update:picker-date="pickerUpdate($event)"
                >
                </v-date-picker>
              </v-menu>
              <v-menu
                ref="multipleDatePickerMenu"
                :close-on-content-click="false"
                v-model="multipleDatePickerMenu"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="290px"
                v-if="selectMultipleDates"
              >
                <template v-slot:activator="{ on }">
                  <v-combobox
                    slot="activator"
                    v-model="formData.market_date"
                    label="Select Market Dates"
                    prepend-icon="mdi-calendar-range"
                    multiple
                    readonly
                    clearable
                    v-on="on"
                    :rules="[rules.required]"
                  >
                    <template v-slot:selection="data">
                      <v-chip
                        small
                        close
                        @click:close="updateMultipleMarketDates(data.item)"
                      > 		
                          {{ getPickerDateString(data.item) }}
                      </v-chip>
                    </template>
                  </v-combobox>
                </template>
                <v-date-picker 
                  v-model="formData.market_date" 
                  :allowed-dates="allowedDates"
                  :multiple="true"
                  no-title 
                  @update:picker-date="pickerUpdate($event)"
                >
                  <v-spacer></v-spacer>
                  <v-btn text color="primary" @click="multipleDatePickerMenu = false">OK</v-btn>
                </v-date-picker>
              </v-menu>
            </v-col>
            <v-col>
              <v-autocomplete
                v-model="formData.stall_id"
                :items="$store.state.stalls"
                label="Stall"
                item-text="stall_title"
                item-value="id"
                :rules="[rules.required]"
                :multiple="selectMultipleStalls"
                :disabled='formData.deleted_date ? true : (bookingType == "invitation" && formAction == "update")'
              >
              </v-autocomplete>
            </v-col>
          </v-row>

          <v-radio-group
            v-model="bookingType"
            row
            label="Booking type:"
            :disabled="formData.deleted_date ? true : false"
          >
            <v-radio
              v-for="(item, i) in bookingTypes"
              color="primary"
              :key="i"
              :label="item.label"
              :value="item.name"
              :disabled="formAction == 'update'"
            >
            </v-radio>
          </v-radio-group>

          <stallholder-select
            v-show="bookingType == 'account' || bookingType == 'invitation'"
            :item="selectedStallholder"
            @change="stallholderChange($event)"
            :disabled='bookingType == "invitation" && formAction == "update"'
          ></stallholder-select>

          <v-alert
            v-if="displayStallholderFields && bookingType == 'account'"
            border="left"
            type="info"
            outlined
            dense
          >
            Any modifications to the following stallholder details only apply to
            this booking. <br /><small
              >(To make permanent changes to a stallholder's future bookings
              update their stallholder account)</small
            >
          </v-alert>

          <v-row v-if="displayStallholderFields">
            <v-col cols="12" lg="4">
              <v-text-field
                v-model="formData.stallholder_stall_name"
                filled
                label="Stall Name"
                :rules="[rules.required]"
              ></v-text-field>
            </v-col>
            <v-col cols="12" lg="4">
              <v-select
                :items="$store.state.categories"
                label="Category"
                filled
                v-model="formData.category"
                item-value="id"
                item-text="name"
                :rules="[rules.required]"
              ></v-select>
            </v-col>
            <v-col cols="12" lg="4">
              <v-text-field
                v-model="formData.email"
                filled
                label="Email address"
                type="email"
                :rules="[rules.email]"
              ></v-text-field>
            </v-col>
          </v-row>

          <v-row class="mt-0 pt-0" v-if="bookingType == 'guest' || (bookingType == 'account' && formAction == 'update')">
            <v-col class="mt-0 pt-0">
              <v-textarea
                v-model="formData.description"
                auto-grow
                filled
                label="Description"
                hint="Short description displayed on Market Map & booking form"
                persistent-hint
                rows="2"
                counter="125"
                :rules="[rules.maxLength(125, formData.description)]"
              ></v-textarea>
            </v-col>
          </v-row>

          <v-row v-if="displayStallholderFields">
            <v-col class="pt-0" cols="12" sm="6" lg="3">
              <v-text-field
                v-model="formData.first_name"
                filled
                label="First Name"
                :rules="[rules.required]"
              ></v-text-field>
            </v-col>
            <v-col class="pt-0" sm="6" cols="12" lg="3">
              <v-text-field
                v-model="formData.last_name"
                filled
                label="Last Name"
                :rules="[rules.required]"
              ></v-text-field>
            </v-col>

            <v-col class="pt-0" cols="12" sm="6" lg="3">
              <v-text-field
                v-model="formData.phone"
                filled
                label="Phone number"
              ></v-text-field>
            </v-col>
            <v-col class="pt-0" cols="12" sm="6" lg="3">
              <v-text-field
                v-model="formData.vehicle_registration"
                filled
                label="Vehicle Reg."
              ></v-text-field>
            </v-col>
          </v-row>

          <!-- <v-row v-if="bookingType == 'invitation'">
            <v-col class="pt-0" cols="12" sm="12" lg="12">
              <v-text-field
                v-model="formData.email"
                filled
                label="Email address"
                type="email"
                :rules="[rules.email]"
              ></v-text-field>
            </v-col>
          </v-row> -->

          <div v-if="bookingType !== 'invitation'">
            <v-row>
              <v-col cols="6" sm="6" lg="3">
                <v-text-field
                  v-model="formData.products.table"
                  filled
                  type="number"
                  label="# tables"
                  :rules="[
                    rules.maxInt(1, formData.products.table),
                    rules.minInt(0, formData.products.table)
                  ]"
                  :disabled="formData.deleted_date ? true : false"
                ></v-text-field>
              </v-col>
              <v-col cols="6" sm="6" lg="3">
                <v-text-field
                  v-model="formData.products.marquee"
                  filled
                  type="number"
                  label="# marquees"
                  :rules="[
                    rules.maxInt(1, formData.products.marquee),
                    rules.minInt(0, formData.products.marquee)
                  ]"
                  :disabled="formData.deleted_date ? true : false"
                ></v-text-field>
              </v-col>
              <v-col cols="6" sm="6" lg="3">
                <v-text-field
                  v-model="formData.products.lunaparkexitticket"
                  filled
                  type="number"
                  label="# Luna Park Single Exit Parking Ticket"
                  :rules="[
                    rules.maxInt(1, formData.products.lunaparkexitticket),
                    rules.minInt(0, formData.products.lunaparkexitticket)
                  ]"
                  :disabled="formData.deleted_date ? true : false"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6" lg="3">
                <v-checkbox
                  v-model="formData.products.insurance"
                  color="primary"
                  label="Insurance Cover?"
                  :disabled="formData.deleted_date ? true : false"
                ></v-checkbox>
              </v-col>
            </v-row>
            <v-row>
              <v-col class="pt-0" cols="12" sm="6" lg="6">
                <v-checkbox
                  v-model="formData.display_profile"
                  color="primary"
                  label="Profile page enabled during this week"
                  :disabled="formData.deleted_date ? true : false"
                ></v-checkbox>
              </v-col>
            </v-row>

            <v-row class="ml-0 mb-2">
              <v-checkbox
                class="mt-0 mr-6"
                v-model="formData.is_paid"
                color="primary"
                label="Paid"
                :disabled="formData.deleted_date ? true : false"
              ></v-checkbox>
              <v-checkbox
                class="mt-0 mr-6"
                v-model="formData.is_cancelled"
                color="primary"
                label="Cancelled"
                :disabled="formData.deleted_date ? true : false"
              ></v-checkbox>
            </v-row>
          </div>

          <v-textarea
            v-model="formData.admin_note"
            auto-grow
            filled
            label="Admin Note"
            rows="3"
            counter="125"
            :rules="[rules.maxLength(125, formData.admin_note)]"
          ></v-textarea>

          <h3 style="margin-bottom: 1.5rem;">Insurance Details</h3>
          <v-row v-if="formData && formData.payment_data && formData.payment_data.insurance_company">
						<v-col class="pt-0" cols="12" sm="6" lg="4">
							<v-text-field v-model="formData.payment_data.insurance_company" filled label="Insurance Company"></v-text-field>
						</v-col>
						<v-col class="pt-0" cols="12" sm="6" lg="4">
							<v-text-field v-model="formData.payment_data.insured_name" filled label="Insured Name"></v-text-field>
						</v-col>
						<v-col class="pt-0" cols="12" sm="6" lg="4">
							<v-text-field v-model="formData.payment_data.insured_address" filled label="Insured Address"></v-text-field>
						</v-col>
						<v-col class="pt-0" cols="12" sm="6" lg="4">
							<v-text-field v-model="formData.payment_data.cover_from" filled label="Cover From"></v-text-field>
						</v-col>
						<v-col class="pt-0" cols="12" sm="6" lg="4">
							<v-text-field v-model="formData.payment_data.cover_to" filled label="Cover To"></v-text-field>
						</v-col>
						<v-col class="pt-0" cols="12" sm="6" lg="4">
							<v-text-field v-model="formData.payment_data.policy_number" filled label="Policy Number"></v-text-field>
						</v-col>
					</v-row>
        </v-form>
        <v-card-actions v-if="showForm">
          <div class="flex-grow-1"></div>
          <v-btn
            color="primary darken-1"
            text
            @click.stop="submitForm()"
            :disabled="isSaveBtnDisabled"
            :loading="isSaveBtnLoading"
          >
            {{ formAction
            }}{{
              bookingType == "invitation" && formAction == "create"
                ? " & Send Invitation"
                : ""
            }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-overlay :value="isSaveBtnLoading" z-index="1000">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
  </v-row>
</template>
<script>
import axios from "axios";
import { mapGetters } from "vuex";
import datePickerMixin from "../js/datePickerMixin";
import StallholderTypeIcon from "../components/StallholderTypeIcon";
import StallholderSelect from "./StallholderSelect";
import { formatDate } from '../js/helpers'

export default {
  data: () => ({
    dialog: "",
    formData: { products: {} },
    isSaveBtnLoading: false,
    isCloseBtnDisabled: false,
    isSaveBtnDisabled: false,
    alert: {
      show: false,
      text: "",
      type: "success",
      dismissible: true
    },
    form: false,
    formAction: "create",
    rules: {
      password: v =>
        (v || "").match(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*(_|[^\w])).+$/) ||
        "Password must contain an upper case letter, a numeric character, and a special character",
      required: v => (Array.isArray(v) ? v.length > 0 : !!v) || "This field is required",
      email: v => /.+@.+/.test(v) || "Please enter a valid email",
      maxLength: (len, v) =>
        (v || "").length <= len || `Invalid character length, maximum ${len}`,
      maxInt: (max, v) => (v || "") <= max || `Invalid number, maximum ${max}`,
      minInt: (min, v) => (v || "") >= min || `Invalid number, minimum ${min}`,
    },
    datepicker: false,
    alert: {
      show: false,
      text: "",
      type: "info",
      dismissible: true
    },
    setupTimeMenu: false,
    stallholderFilter: (item, queryText, itemText) => {
      let searchString = `${item.stall_name} ${item.first_name} ${
        item.last_name
      } ${item.email}`;
      return searchString.toLowerCase().indexOf(queryText.toLowerCase()) > -1;
    },
    bookingType: "account",
    stallholderAccountHasChanged: false,
    multipleDatePickerMenu: false,
    showForm: true,
    infoText: ''
  }),
  props: {
    dialogVisible: {
      type: Boolean,
      default: false,
      required: false
    },
    selectedItem: {
      required: true
    }
  },
  mixins: [datePickerMixin],
  components: {
    StallholderTypeIcon,
    StallholderSelect
  },
  watch: {
    dialogVisible: {
      immediate: true,
      handler(val, oldVal) {
        if (val)
          this.$store.commit("updatePickerAvailableMarketsDates", {
            dates: [this.selectedItem.market_date]
          }); // so we dont have to wait for picker to fetch all dates in year
      }
    }
  },
  methods: {
    closeDialog() {
      this.$emit("close");
      // wait half a sec, before resetting data (otherwise we see it cleared before the dialog fully transitions)
      setTimeout(() => {
        this.isSaveBtnLoading = false;
        this.formdata = {};
        this.resetAlert();
        //this.resetForm();
      }, 500);
    },
    resetForm() {
      //this.$refs.form.reset()
      // this.formAction = 'create'; //reset to default
      // this.bookingType = 'account';
    },
    submitForm() {
      this.resetAlert();

      if (this.$refs.form.validate()) this.updateBooking();
    },
    updateBooking() {
      this.savingDialog = true;
      let self = this;
      this.isSaveBtnLoading = true;

      let isUpdatingId = "";

      if (this.formData.id && this.formData.id !== 0)
        isUpdatingId = this.formData.id;

      if (this.bookingType == "guest")
        this.formData.stallholder_id = 0;

      if (this.bookingType == "invitation")
        this.formData.booking_type = "invitation";

      axios({
        method: "POST",
        url: `/wisdom-markets/v1/bookings/${isUpdatingId}`,
        headers: {
          Authorization: `Bearer ${this.$store.state.accessToken}`
        },
        data: this.formData
      })
        .then(response => {
          console.log("saved", response);
          this.isSaveBtnLoading = false;

          this.savingDialog = false;

          if(!Array.isArray(response.data) && (this.selectMultipleDates || this.selectMultipleStalls) ) // if user has used the main (bulk) 'booking a stall' form but only made 1 booking
            response.data = [response.data]; // make response an array, so we can do the "batch" result below (instead of trying to populate that one booking into the existing booking form, just show the message)
          
          if(Array.isArray(response.data)){
            this.showForm = false;
            if(response.data[0].booking_type === 'invitation' )
              this.infoText = `<p>Invitation for <strong>${response.data.length}</strong> stall${response.data.length > 1 ? 's': ''} created for <strong>${response.data[0].stallholder_stall_name}</strong> emailed to ${response.data[0].email}</p>`
            else
              this.infoText = `<p><strong>${response.data.length}</strong> booking${response.data.length > 1 ? 's': ''} created for <strong>${response.data[0].stallholder_stall_name}</strong></p>`

            this.infoText += `<ul>`;
            response.data.forEach(b => {
              this.infoText += `<li>${formatDate(b.market_date)} - ${this.getStallName(b.stall_id)} ${b.booking_type === 'invitation' ? `(expires ${b.expiry_date})` : ''}</li>`
            })
            this.infoText += `</ul>`;
          }
          
          self.showAlert("success", "Saved!");

          if (this.formAction == "create") {
            this.$emit("create", response.data);
          } else {
            this.$emit("update", response.data);
            this.$store.dispatch("updateBooking", response.data);
          }
          const products = {...response.data.products};
          this.formData = Object.assign({}, response.data, { products }); // assign the response to formdata (incase user tries to update the booking straight after creation (ie before closing dialog) OR (incase you've just switched stallholders and the stallholder deets need to be populated)

          this.formAction = "update";

          this.stallholderAccountHasChanged = false; //reset this var so that the fields are shown with the new response data
        })
        .catch(function(error) {
          console.log("error saving", error);
          //todo display error popup
          self.isSaveBtnLoading = false;
          // this.isCloseBtnDisabled = false;
          self.savingDialog = false;

          let message = "Error!";

          if (error.response.data && error.response.data.message)
            message =
              message + " " + JSON.stringify(error.response.data.message);

          self.showAlert("error", message);
        });
    },
    stallholderChange(stallholder) {
      console.error(stallholder);
      if (stallholder && stallholder.id) {
        this.formData.stallholder_id = stallholder.id;
        this.stallholderAccountHasChanged = true;
      } else {
        this.formData.stallholder_id = undefined;
      }
    },
    showAlert(type, text) {
      this.alert.show = true;
      this.alert.text = text;
      this.alert.type = type;
    },
    resetAlert() {
      //and reset
      this.alert = {
        show: false,
        text: "",
        type: "info",
        dismissible: true
      };
    },
    updateMultipleMarketDates (date) {
      if(!date) return;
       
      const index = this.formData.market_date.findIndex(x => x===date)

      if (index === -1)
         this.formData.market_date.push(date);      
      else 
        this.formData.market_date.splice(index,1);
    },
     getPickerDateString: function(date) {
      let weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      
      const d = new Date(date);
      const dayOfWeek = d.getUTCDay();
      
      return `${weekdays[dayOfWeek]}, ${d.getUTCDate()}/${d.getUTCMonth() + 1}`;
    }
  },
  computed: {
    //
    isDialogVisible() {
      if (this.dialog != this.dialogVisible) {
        return this.dialogVisible;
      } else {
        return this.dialog;
      }
    },
    ...mapGetters(["getStallholderColor", "getStallholderById", "getStallName"]),
    selectedDateString() {
      if (!this.selectedItem) return "";

      let date = new Date(this.selectedItem.market_date);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric"
      });
    },
    formattedDate: {
      get: function() {
        if (this.dialogVisible) {
          // we do this condition so it forces a rerender otherwise when we open a 2nd booking with the same date as previous one, the date field appears empty (even thought the computed property is correct)
          if (!this.formData || !this.formData.market_date) return "";

          let date = new Date(this.formData.market_date);
          return date.toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric"
          });
        }
      },
      set: function(newValue) {
        // needed a setter for when we close the dialog window, we get missing setter function errors otherwise (even though we don't really need it)
      }
    },
    displayStallholderFields() {
      return (
        this.bookingType == "guest" ||
        (this.bookingType == "account" &&
          this.formAction == "update" &&
          !this.stallholderAccountHasChanged)
      );
    },
    bookingTypes() {
      let types = [
        { label: "Account holder", name: "account" },
        { label: "Guest (Casual, no account)", name: "guest" }
      ];

      if (this.formAction == "create" || this.bookingType == "invitation")
        types.push({ label: "Invitation", name: "invitation" });

      return types;
    },
    selectedStallholder() {
      if (!this.selectedItem.stallholder_id) return {};

      return {
        id: this.selectedItem.stallholder_id,
        stall_name: this.selectedItem.stallholder_stall_name,
        first_name: this.selectedItem.first_name,
        last_name: this.selectedItem.last_name,
        stallholder_type: this.selectedItem.stallholder_type
      };
    },
    selectMultipleStalls() {
      return !Number.isInteger(this.selectedItem.stall_id);
    },
    selectMultipleDates() {
      return this.selectMultipleStalls;
    }
  },
  mounted() {
    //the bookingmodal now has a key based on booking or stallid - changing Key forces re-render of a component
    const products = {...this.selectedItem.products};
    this.formData = Object.assign({}, this.selectedItem, { products });

    if(this.selectMultipleDates)
      this.formData.market_date = [ this.selectedItem.market_date ]

    if (this.selectedItem.id) {
      this.formAction = "update";

      if (!this.selectedItem.stallholder_id)
        this.bookingType = "guest";

      if (this.selectedItem.expiry_date)
        this.bookingType = "invitation";

    }
  }
};
</script>
